import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Facility } from '@/entities/Facility';
import { Connection } from '@/services/Connection';
import { Logger } from '@/services/Logger';
import { FacilitySearchReqMsg } from '@/services/signalr/MessageTypes/Facility';
import { Signalr } from '@/services/signalr/Signalr';
import { FacilityStore } from '@/stores/FacilityStore';
import { getDepartmentById } from '@/utils/getDepartments';
import { getFacilityDepartmentName, isMeaningfulDepartment } from '@/utils/getFacilityDepartmentName';
import { orderByField } from '@/utils/orderBy';
import { IFacilityDepartmentInfo } from '~/api/IFacilityDepartmentInfo';
import { IFacilityInfo } from '~/api/IFacilityInfo';
import { IGeoPosition } from '~/api/IGeoPosition';
import type { IFacilitySearchRespMsg } from '~/api/messages/IFacilitySearchRespMsg';
import type { IFacilityUpdateRespMsg } from '~/api/messages/IFacilityUpdateRespMsg';

// A department resolved for display, decoupled from any UI concern. The manager owns
// the logic of identifying which facilities have departments and how those departments
// are sourced (hybrid vs standalone), leaving presentation/formatting to the caller.
export interface IFacilityDepartmentMatch {
  // Representative facility used for the expand/collapse group header.
  facility: IFacilityInfo;
  // Facility carrying the department's own contact details.
  // Hybrid: the separate department row. Standalone/Portal: the parent facility.
  departmentFacility: IFacilityInfo;
  departmentTypeId: string;
  departmentName: string;
  address1: string;
  address2: string;
  city: string;
  region: string;
  postalCode: string;
  phone: string;
  position: IGeoPosition;
}

@SignalrListener
@service(FacilityManager)
export class FacilityManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(FacilityStore) protected facilityStore: FacilityStore;
  @inject(Connection) protected connection: Connection;
  protected latestSearchToken = 0;

  find(id?: string) {
    return this.facilityStore.find(id);
  }

  async search(searchParameter: string, includeDisabled = false) {
    this.latestSearchToken += 1;
    const searchToken = this.latestSearchToken;

    this.reset();
    const response = await this.signalr.invoke(FacilitySearchReqMsg, { searchParameter, includeDisabled });
    if (searchToken !== this.latestSearchToken) {
      return;
    }

    this.update(response.facilities);
  }

  get all() {
    const facilities = this.facilityStore.all()
      .filter((facility) => facility.status !== STATUS.DISABLED);
    return orderByField(facilities, this.facilityStore.valueKey);
  }

  // Resolves the department name to display for a given facility (e.g. a trip leg's
  // facility). In hybrid the department is a plain string on the facility; a trip leg
  // may omit it, so we fall back to the stored facility row (from ListFacilityMsg)
  // keyed by id. Standalone/portal read the nested `facilityDepartments`.
  resolveDepartmentName(facility?: Partial<IFacilityInfo> | null): string | null {
    if (!facility) return null;

    if (this.connection.isHybrid) {
      if (isMeaningfulDepartment(facility.department)) return facility.department as string;
      const stored = facility.id ? this.find(facility.id) : undefined;
      return isMeaningfulDepartment(stored?.department) ? (stored?.department as string) : null;
    }

    return getFacilityDepartmentName(facility, false);
  }

  // Identifies the facility departments matching a search term. Grouping differs by
  // deployment: hybrid delivers each department as a separate same-named facility row,
  // while standalone/portal nest departments inside `facilityDepartments`.
  getDepartmentMatches(searchTerm: string): IFacilityDepartmentMatch[] {
    return this.connection.isHybrid
      ? this.getHybridDepartmentMatches(searchTerm)
      : this.getStandaloneDepartmentMatches(searchTerm);
  }

  // Plain domain address used for term matching (UI-independent, no override text).
  protected static abbreviatedAddress(facility: IFacilityInfo): string {
    const address = facility.address1 || '';
    const city = facility.city || '';
    if (address && city) return `${address}, ${city}`;
    return address || city || '';
  }

  // Hybrid (RNDB) delivers the base facility and each department as SEPARATE rows that
  // share a name. Base = facilityType 1 with no department; departments = facilityType 3
  // carrying a `department` string. Group them by name so departments nest under a header.
  protected getHybridDepartmentMatches(searchTerm: string): IFacilityDepartmentMatch[] {
    const term = searchTerm.toLowerCase();

    const groups = new Map<string, { base?: IFacilityInfo; depts: IFacilityInfo[] }>();
    this.all.forEach((facility) => {
      const key = (facility.name || '').toLowerCase();
      const group = groups.get(key) ?? { base: undefined, depts: [] };
      if (isMeaningfulDepartment(facility.department)) {
        group.depts.push(facility);
      }
      else {
        group.base ??= facility;
      }
      groups.set(key, group);
    });

    const matches: IFacilityDepartmentMatch[] = [];
    groups.forEach((group) => {
      // A facility with no department rows stays a plain (flat) facility, not a group.
      if (!group.depts.length) return;

      const representative = group.base ?? group.depts[0];
      const nameMatch = representative.name?.toLowerCase().includes(term);
      const groupAddress = FacilityManager.abbreviatedAddress(representative).toLowerCase();

      group.depts
        .map((deptFacility) => ({ deptFacility, deptName: deptFacility.department as string }))
        .sort((left, right) => left.deptName.localeCompare(right.deptName, undefined, { sensitivity: 'base' }))
        .forEach(({ deptFacility, deptName }) => {
          const addressMatch = deptFacility.address1?.toLowerCase().includes(term) ||
            groupAddress.includes(term);
          const deptMatch = deptName.toLowerCase().includes(term);
          if (!nameMatch && !addressMatch && !deptMatch) return;

          matches.push({
            facility: representative,
            departmentFacility: deptFacility,
            departmentTypeId: '',
            departmentName: deptName,
            address1: deptFacility.address1 ?? '',
            address2: deptFacility.address2 ?? '',
            city: deptFacility.city ?? '',
            region: deptFacility.region ?? '',
            postalCode: deptFacility.postalCode ?? '',
            phone: deptFacility.phoneNumber ?? '',
            position: deptFacility.position ?? { lat: 0, lng: 0 },
          });
        });
    });

    return matches;
  }

  // Standalone/Portal store departments inside `facilityDepartments`; department names
  // are resolved from their type id.
  protected getStandaloneDepartmentMatches(searchTerm: string): IFacilityDepartmentMatch[] {
    const term = searchTerm.toLowerCase();
    const matches: IFacilityDepartmentMatch[] = [];

    this.all.forEach((facility) => {
      const activeDepts = (facility.facilityDepartments || [])
        .filter((fd: IFacilityDepartmentInfo) => fd.status !== STATUS.DISABLED);
      if (!activeDepts.length) return;

      const nameMatch = facility.name?.toLowerCase().includes(term);
      const displayAddress = FacilityManager.abbreviatedAddress(facility).toLowerCase();

      activeDepts
        .map((fd: IFacilityDepartmentInfo) => ({
          fd,
          deptName: getDepartmentById(fd.departmentTypeId)?.value ?? fd.departmentTypeId ?? '',
        }))
        .sort((left, right) => left.deptName.localeCompare(right.deptName, undefined, { sensitivity: 'base' }))
        .forEach(({ fd, deptName }) => {
          const addressMatch = facility.address1?.toLowerCase().includes(term) ||
            displayAddress.includes(term) ||
            fd.address?.toLowerCase().includes(term);
          const deptMatch = deptName.toLowerCase().includes(term);
          if (!nameMatch && !addressMatch && !deptMatch) return;

          matches.push({
            facility,
            departmentFacility: facility,
            departmentTypeId: fd.departmentTypeId || '',
            departmentName: deptName,
            address1: fd.address ?? '',
            address2: fd.address2 ?? '',
            city: fd.city ?? '',
            region: fd.region ?? '',
            postalCode: fd.postalCode ?? '',
            phone: fd.phone ?? '',
            position: fd.position ?? { lat: 0, lng: 0 },
          });
        });
    });

    return matches;
  }

  reset() {
    this.facilityStore.set([]);
  }

  update(facilities: IFacilityInfo[]) {
    const normalizedFacilities = facilities.map((facility) => new Facility(facility));
    this.facilityStore.batchUpdate(normalizedFacilities);
    return facilities;
  }

  @SignalrMessage('ListFacilityMsg')
  protected onListFacilityMsg(response: IFacilitySearchRespMsg) {
    this.update(response.facilities);
  }

  @SignalrMessage('FacilityUpdateMsg')
  protected onFacilityUpdateMsg(response: IFacilityUpdateRespMsg) {
    this.update([response.facility]);
  }
}
